import { Task } from 'app/data/types/tasks'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import {isWeb} from '@my/ui'

interface TimerState {
  duration: number // in seconds
  startTimestamp: number | null // timestamp when timer started
  isRunning: boolean
  isPaused: boolean
  pausedTimeRemaining: number | null // time remaining when paused
  activeTask: Task | null // full task object for immediate display
  projectName: string | null // name of associated project

  // Actions
  start: () => void
  pause: () => void
  reset: () => void
  clearTimer: () => void
  addMinute: () => void
  subtractMinute: () => void
  setDuration: (seconds: number) => void
  getRemainingTime: () => number
  setActiveTask: (task: Task, projectName: string) => void
  updateStepStatus: (stepId: string, status: 'completed' | 'incomplete') => void
  clearActiveTask: () => void
}

// Add hydration state
interface TimerStateWithHydration extends TimerState {
  _hasHydrated: boolean
  setHasHydrated: (state: boolean) => void
}

const timerStore = (set: any, get: any): TimerStateWithHydration => ({
      duration: 30 * 60, // 30 minutes in seconds
      startTimestamp: null,
      isRunning: false,
      isPaused: false,
      pausedTimeRemaining: null,
      activeTask: null,
      projectName: null,
      _hasHydrated: false,

      getRemainingTime: () => {
        const state = get()
        if (!state.isRunning) {
          return state.pausedTimeRemaining ?? state.duration
        }

        if (!state.startTimestamp) return state.duration

        const elapsedSeconds = Math.floor((Date.now() - state.startTimestamp) / 1000)
        return Math.max(0, state.duration - elapsedSeconds)
      },

      start: () =>
        set((state) => {
          const now = Date.now()
          const remainingTime = state.pausedTimeRemaining ?? state.duration
          // Calculate what the start time would have been
          const adjustedStartTime = now - (state.duration - remainingTime) * 1000

          return {
            isRunning: true,
            isPaused: false,
            startTimestamp: adjustedStartTime,
            pausedTimeRemaining: null,
          }
        }),

      pause: () =>
        set((state) => ({
          isRunning: false,
          isPaused: true,
          pausedTimeRemaining: state.getRemainingTime(),
          startTimestamp: null,
        })),

      reset: () =>
        set((state) => ({
          isRunning: false,
          isPaused: false,
          startTimestamp: null,
          pausedTimeRemaining: null,
          // Keep current duration - don't reset it
          // Preserve active task information
          activeTask: state.activeTask,
          projectName: state.projectName,
        })),

      clearTimer: () =>
        set(() => ({
          isRunning: false,
          isPaused: false,
          startTimestamp: null,
          pausedTimeRemaining: null,
          duration: 30 * 60, // Reset to 30 minutes
          activeTask: null,
          projectName: null,
        })),

      addMinute: () =>
        set((state) => {
          //console.log('addMinute function called in store', { currentDuration: state.duration, isRunning: state.isRunning })
          const newDuration = state.duration + 60
          const remainingTime = state.getRemainingTime() + 60

          if (state.isRunning) {
            // Adjust start timestamp to account for added time
            const now = Date.now()
            const adjustedStartTime = now - (newDuration - remainingTime) * 1000
            return {
              duration: newDuration,
              startTimestamp: adjustedStartTime,
            }
          }

          return {
            duration: newDuration,
            pausedTimeRemaining: state.isPaused ? remainingTime : null,
          }
        }),

      subtractMinute: () =>
        set((state) => {
          const newDuration = Math.max(60, state.duration - 60)
          const remainingTime = Math.max(0, state.getRemainingTime() - 60)

          if (state.isRunning) {
            // Adjust start timestamp to account for subtracted time
            const now = Date.now()
            const adjustedStartTime = now - (newDuration - remainingTime) * 1000
            return {
              duration: newDuration,
              startTimestamp: adjustedStartTime,
            }
          }

          return {
            duration: newDuration,
            pausedTimeRemaining: state.isPaused ? remainingTime : null,
          }
        }),

      setDuration: (seconds) =>
        set({
          duration: seconds,
          pausedTimeRemaining: null,
          startTimestamp: null,
          isRunning: false,
          isPaused: false,
        }),

      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        })
      },

      setActiveTask: (task, projectName) =>
        set((state) => {
          const isSameTask = state.activeTask?.id === task.id

          if (isSameTask) {
            // Same task - preserve timer state, just update task data
            return {
              projectName,
              activeTask: task,
              // Keep existing: duration, isRunning, isPaused, startTimestamp, pausedTimeRemaining
            }
          } else {
            // Different task - reset timer completely
            const duration = task.duration ? task.duration * 60 : 30 * 60 // convert minutes to seconds
            return {
              projectName,
              activeTask: task,
              duration,
              startTimestamp: null,
              isRunning: false,
              isPaused: false,
              pausedTimeRemaining: null,
            }
          }
        }),

      updateStepStatus: (stepId, status) =>
        set((state) => {
          if (!state.activeTask?.steps) return state

          const updatedSteps = state.activeTask.steps.map(step =>
            step.id === stepId ? { ...step, status } : step
          )

          return {
            activeTask: {
              ...state.activeTask,
              steps: updatedSteps
            }
          }
        }),

      clearActiveTask: () =>
        set({
          activeTask: null,
          projectName: null,
        }),
    })

export const useTimerStore = create<TimerStateWithHydration>()(
  persist(
    timerStore,
    {
      name: 'focus-timer',
      storage: createJSONStorage(() => {
        // Cross-platform storage that handles both web and native
        if (typeof window !== 'undefined' && window.localStorage) {
          // Web environment
          return {
            getItem: (name: string) => Promise.resolve(localStorage.getItem(name)),
            setItem: (name: string, value: string) =>
              Promise.resolve(localStorage.setItem(name, value)),
            removeItem: (name: string) => Promise.resolve(localStorage.removeItem(name)),
          }
        } else if (isWeb) {
          // During SSR, use no-op storage simply to avoid errors
          return {
            getItem: () => Promise.resolve(null),
            setItem: () => Promise.resolve(),
            removeItem: () => Promise.resolve(),
          }
        } else {
          // Native environment - use AsyncStorage
          const AsyncStorage = require('@react-native-async-storage/async-storage').default
          return AsyncStorage
        }
      }),
      partialize: (state) => ({
        duration: state.duration,
        pausedTimeRemaining: state.pausedTimeRemaining,
        activeTask: state.activeTask,
        projectName: state.projectName,
      }),
      onRehydrateStorage: () => async (state) => {
        if (state) {
          state.setHasHydrated(true)
        } else {
          // Check if storage key exists to distinguish between no data vs parsing error
          try {
            let storedData: string | null = null
            if (typeof window !== 'undefined' && window.localStorage) {
              // Web environment
              storedData = localStorage.getItem('focus-timer')
            } else {
              // Native environment
              const AsyncStorage = require('@react-native-async-storage/async-storage').default
              storedData = await AsyncStorage.getItem('focus-timer')
            }

            if (storedData) {
              console.warn('[Timer Storage] Failed to parse stored data - using default state')
            }
            // If no stored data, this is normal (first launch or cleared storage)
          } catch (error) {
            console.warn('[Timer Storage] Failed to access storage during rehydration:', error)
          }

          // Always mark as hydrated, even when state is null
          // Use setTimeout to ensure store is initialized
          setTimeout(() => {
            useTimerStore.getState().setHasHydrated(true)
          }, 0)
        }
      },
    }
  )
)

// Add a helper hook to check hydration status
export const useHydrated = () => useTimerStore((state) => state._hasHydrated)
